import { readSavedScrollPosition } from '@monorepo/shared/utils/navigation/scrollPositionStorage';
import { startScrollPositionRestore } from '@monorepo/shared/utils/navigation/scrollPositionRestore';
import { useEffect, useRef } from 'react';
import { useSaveScrollPosition } from './useSaveScrollPosition';

type ScrollPositionParams = {
  storageKey: string;
  isReady: boolean;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  contentKey?: string;
};

/**
 * Persists scroll position to sessionStorage and restores it once content height
 * has stabilized so async widgets do not leave the viewport shifted.
 */
export function useRestoreScrollPosition({
  storageKey,
  isReady,
  scrollContainerRef,
  contentKey,
}: ScrollPositionParams) {
  // when true, restore is complete and ready to be saved
  // false is set right before restore starts to block saves
  // during programmatic scrolling
  const allowScrollSaveRef = useRef(true);

  useSaveScrollPosition(
    storageKey,
    isReady,
    scrollContainerRef,
    allowScrollSaveRef,
  );

  useEffect(() => {
    const container = scrollContainerRef?.current;

    if (!storageKey || !isReady || !container) {
      return undefined;
    }

    const savedScrollTop = readSavedScrollPosition(storageKey);
    if (savedScrollTop == null) {
      allowScrollSaveRef.current = true;
      return undefined;
    }

    allowScrollSaveRef.current = false;

    return startScrollPositionRestore({
      container,
      storageKey,
      savedScrollTop,
      allowScrollSave: allowScrollSaveRef,
    });
  }, [contentKey, isReady, scrollContainerRef, storageKey]);
}
